/* eslint-disable no-nested-ternary */
import React from 'react';
import classNames from 'classnames';
import { get } from 'lodash-es';
import './index.scss';
import MetricAndUpDown from '@/pages/dashboard/components/MetricAndUpDown';
import { IPercentageCellProps } from './type';
import { formatPercentNum, getUpAndDownStatus } from '@/pages/dashboard/utils/utils';
import { ProgressBar } from './components/ProgressBar';
import { IProgressBarProps } from './components/ProgressBar/type';
import { NumberCell } from '../NumberCell';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import { renderSlot } from '@/pages/dashboard/components/CommonTable/utils';
import { processChange } from '@/pages/dashboard/components/CommonTable/components/CommonTableCell/PercentageCell/utils';

export const PercentageCell = (props: IPercentageCellProps) => {
  const { className, style, value, formattedValue, record, config } = props;

  if (config?.showPercentageAsChangeRate) {
    config.showPercentage = false;
    config.showChangeRate = false;
  }

  const {
    showPercentage = true,
    showChangeRate = false,
    showPercentageAsChangeRate = false,
    showProgressBar = false,
    showNegativeSign = true,
    progressBarProps = {},
    changeAlign,
    trendLabelMap,
    changeKey = '',
    formatDodAsPercent = false,
    dodDecimals = 1,
    bubble,
  } = config || {};

  const {
    label,
    labelEn,
    labelCn,
    status: mapStatus,
  } = trendLabelMap?.[formattedValue] || {};

  const { contentKey = '' } = bubble || {};

  const { isEN } = useLocal();

  const change = showChangeRate ? (get(record, changeKey) ?? undefined) : showPercentageAsChangeRate ? formattedValue : undefined;
  /** 环比展示：对象型保留 show_value；仅 formatDodAsPercent 时对数值走 formatPercentNum，否则与历史一致用 processChange */
  const resolveDodDisplay = (): string | number | undefined => {
    if (!showChangeRate || !changeKey) return undefined;
    const raw = get(record, changeKey);
    if (raw !== undefined && raw !== null && typeof raw === 'object') {
      if ('show_value' in (raw as Record<string, unknown>)) {
        const sv = (raw as { show_value?: string; }).show_value;
        return sv === undefined || sv === '' ? undefined : sv;
      }
      return '-';
    }
    if (!formatDodAsPercent) {
      return processChange(change, { showNegativeSign });
    }
    const str = String(raw ?? '');
    if (str.includes('%')) {
      return processChange(str, { showNegativeSign });
    }
    return formatPercentNum(parseFloat(str), dodDecimals, true);
  };
  const rawChange = showChangeRate
    ? ((): unknown => {
      const raw = get(record, changeKey);
      if (raw !== null && typeof raw === 'object' && 'this_value' in (raw as Record<string, unknown>)) {
        return (raw as { this_value?: string | number; }).this_value;
      }
      return raw;
    })()
    : showPercentageAsChangeRate
      ? value
      : undefined;

  const bubbleContent = bubble
    ? renderSlot(bubble.content, 'dbd-common-table__percentage-cell__bubble', 'bubble', { record: get(record, contentKey) })
    : undefined;

  return (
    <NumberCell className={classNames('dbd-common-table__percentage-cell', className)} style={style} config={config}>
      {(showProgressBar || Object.keys(progressBarProps || {}).length > 0) && (() => {
        // 支持"按列基准归一化"：progressBarProps.widthKey 指向 record 中预计算好的宽度百分比字段
        const {
          widthKey,
          basedOnColumnRange,
          colorByColumnRange,
          positiveColor,
          negativeColor,
          mode,
          height,
          ...restBarProps
        } = progressBarProps as IProgressBarProps & { widthKey?: string; basedOnColumnRange?: boolean; };
        const widthFromRecord = widthKey ? get(record, widthKey) : undefined;
        const widthValue =
          typeof widthFromRecord === 'number' && Number.isFinite(widthFromRecord)
            ? widthFromRecord
            : undefined;
        // basedOnColumnRange 场景下，原始 value 是绝对差值（如环比 PP 等），不应再乘 100
        const barValue = basedOnColumnRange ? value : value * 100;
        return (
          <ProgressBar
            {...restBarProps}
            value={barValue}
            widthValue={widthValue}
            colorByColumnRange={colorByColumnRange}
            positiveColor={positiveColor}
            negativeColor={negativeColor}
            mode={mode}
            height={height}
          />
        );
      })()}
      <MetricAndUpDown
        className={classNames('dbd-common-table__percentage-cell__metric_and_up_down', {
          'dbd-common-table__percentage-cell__show-percentage-as-change-rate': showPercentageAsChangeRate,
          'dbd-common-table__percentage-cell__show-percentage-as-change-rate-left': showPercentageAsChangeRate && changeAlign === 'left',
          'dbd-common-table__percentage-cell__show-percentage-as-change-rate-center': showPercentageAsChangeRate && changeAlign === 'center',
        })}
        metric={showPercentage ? formattedValue : undefined}
        updown={showChangeRate || showPercentageAsChangeRate
          ? {
            data: ((isEN ? labelEn : labelCn) || label || (showChangeRate
              ? resolveDodDisplay()
              : processChange(change, {
                showNegativeSign,
              }))) ?? '-',
            status: mapStatus || getUpAndDownStatus(rawChange, false)
          }
          : undefined
        }
        hasTooltip={false}
        bubble={bubble ? { ...bubble, content: bubbleContent } : undefined}
      />
    </NumberCell>
  );
};
